import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "./ui/button";
import PropTypes from "prop-types";
import API_BASE_URL from "../services/api";
import logger from "../utils/logger";

const formatMoney = (value) =>
  typeof value === "number" ? `$${value.toFixed(2)}` : "—";

const OrderConfirmation = ({ setCartItems }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");

  // status: verifying | confirmed | unpaid | error
  const [status, setStatus] = useState(sessionId ? "verifying" : "error");
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (!sessionId) return;

    let cancelled = false;

    const verifyOrder = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/stripe/order/session/${encodeURIComponent(sessionId)}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            },
          },
        );
        const data = await response.json().catch(() => ({}));
        if (cancelled) return;

        if (response.ok && data.paid && data.order) {
          setOrder(data.order);
          setStatus("confirmed");
          // Clear the cart ONLY once the backend confirms payment succeeded.
          setCartItems([]);
          localStorage.removeItem("cartItems");
          logger.info("OrderConfirmation: payment verified, cart cleared", {
            orderId: data.order._id,
          });
        } else if (response.status === 402) {
          setStatus("unpaid");
        } else {
          logger.error("OrderConfirmation: verification failed", {
            status: response.status,
            error: data.error,
          });
          setStatus("error");
        }
      } catch (error) {
        if (cancelled) return;
        logger.error("OrderConfirmation: verification request failed", {
          error: error.message,
        });
        setStatus("error");
      }
    };

    verifyOrder();
    return () => {
      cancelled = true;
    };
  }, [sessionId, setCartItems]);

  if (status === "verifying") {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4 py-16 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 dark:border-blue-400 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300 text-lg">
            Confirming your payment…
          </p>
        </div>
      </div>
    );
  }

  if (status !== "confirmed") {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 text-center">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4 font-handwritten">
              {status === "unpaid"
                ? "Payment not completed"
                : "We couldn't confirm your order"}
            </h1>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              {status === "unpaid"
                ? "Your payment hasn't completed, so no order was placed. Your cart is untouched — you can try checking out again."
                : "We couldn't verify this order. If you completed a payment, it's safe — check your order history or contact support. Your cart has not been cleared."}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate("/checkout")}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
              >
                Return to Checkout
              </Button>
              <Button
                onClick={() => navigate("/order-history")}
                variant="outline"
                className="px-6 py-2 rounded-lg transition-colors"
              >
                View My Orders
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const shipping = order.shippingAddress || {};

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-green-600 dark:text-green-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2 font-handwritten">
                Thank You for Your Order!
              </h1>
              <p className="text-gray-600 dark:text-gray-300">
                Order number:{" "}
                <span className="font-mono font-semibold">{order._id}</span>
              </p>
              {order.email && (
                <p className="text-gray-600 dark:text-gray-300 mt-1">
                  A confirmation email will be sent to{" "}
                  <span className="font-semibold">{order.email}</span>
                </p>
              )}
            </div>

            {/* Line items */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                Your items
              </h2>
              <ul className="divide-y divide-gray-200 dark:divide-gray-600">
                {(order.orderItems || []).map((item, index) => (
                  <li
                    key={`${item.name}-${index}`}
                    className="py-3 flex items-center justify-between"
                  >
                    <div className="flex items-center space-x-3 min-w-0">
                      {item.image ? (
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-12 h-12 object-cover rounded-md flex-shrink-0"
                        />
                      ) : null}
                      <div className="min-w-0">
                        <p className="text-gray-900 dark:text-white truncate">
                          {item.name}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Qty: {item.quantity}
                          {item.size ? ` · Size: ${item.size}` : ""}
                          {item.color ? ` · Color: ${item.color}` : ""}
                        </p>
                      </div>
                    </div>
                    <p className="text-gray-900 dark:text-white flex-shrink-0 ml-3">
                      {formatMoney(item.price * item.quantity)}
                    </p>
                  </li>
                ))}
              </ul>
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600 space-y-1 text-sm">
                <div className="flex justify-between text-gray-700 dark:text-gray-300">
                  <span>Subtotal</span>
                  <span>{formatMoney(order.itemsPrice)}</span>
                </div>
                <div className="flex justify-between text-gray-700 dark:text-gray-300">
                  <span>Shipping</span>
                  <span>{formatMoney(order.shippingPrice)}</span>
                </div>
                <div className="flex justify-between text-gray-700 dark:text-gray-300">
                  <span>Tax</span>
                  <span>{formatMoney(order.taxPrice)}</span>
                </div>
                <div className="flex justify-between text-base font-bold text-gray-900 dark:text-white pt-2 border-t border-gray-200 dark:border-gray-600">
                  <span>Total</span>
                  <span>{formatMoney(order.totalPrice)}</span>
                </div>
              </div>
            </div>

            {/* Shipping address */}
            {shipping.street && (
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 mb-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                  Shipping to
                </h2>
                <address className="not-italic text-gray-700 dark:text-gray-300">
                  {shipping.name && <div>{shipping.name}</div>}
                  <div>{shipping.street}</div>
                  {shipping.street2 && <div>{shipping.street2}</div>}
                  <div>
                    {shipping.city}, {shipping.state} {shipping.zipCode}
                  </div>
                  <div>{shipping.country}</div>
                </address>
              </div>
            )}

            <p className="text-gray-600 dark:text-gray-300 text-center mb-6">
              We&apos;ll email you tracking information once your order ships.
              You can follow its progress any time in{" "}
              <Link
                to="/order-history"
                className="text-blue-600 dark:text-blue-400 underline"
              >
                your order history
              </Link>
              .
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate("/shop")}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
              >
                Continue Shopping
              </Button>
              <Button
                onClick={() => navigate("/order-history")}
                variant="outline"
                className="px-6 py-2 rounded-lg transition-colors"
              >
                View My Orders
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

OrderConfirmation.propTypes = {
  setCartItems: PropTypes.func.isRequired,
};

export default OrderConfirmation;
